import { computed } from 'vue'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'

export function useGroupNames() {
  const authStore = getActivePinia() ? useAuthStore() : null

  /**
   * Computed mapping of groupId (lowercase) -> displayName
   */
  const groupNameMap = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    const userInfo = authStore?.userInfo

    if (!userInfo) return map

    // 1. User's group details from IdP
    if (userInfo.user?.groupDetails) {
      for (const detail of userInfo.user.groupDetails) {
        if (detail.id && detail.displayName) {
          map[detail.id.toLowerCase().trim()] = detail.displayName
        }
      }
    }

    // 2. Allowed tenants' group name mappings
    if (userInfo.allowedTenants) {
      for (const tenant of userInfo.allowedTenants) {
        if (tenant.groupNameMapping) {
          for (const [groupId, name] of Object.entries(tenant.groupNameMapping)) {
            if (groupId && name) {
              map[groupId.toLowerCase().trim()] = name
            }
          }
        }
      }
    }

    // 3. Active tenant's group name mappings (highest priority for active tenant)
    if (userInfo.activeTenant?.groupNameMapping) {
      for (const [groupId, name] of Object.entries(userInfo.activeTenant.groupNameMapping)) {
        if (groupId && name) {
          map[groupId.toLowerCase().trim()] = name
        }
      }
    }

    return map
  })

  /**
   * Resolves a groupId to its human-readable display name.
   * Returns the original groupId if no display name is found.
   */
  function resolveGroupName(groupId: string | null | undefined): string {
    if (!groupId) return ''
    const trimmed = groupId.trim()
    if (!trimmed) return ''
    return groupNameMap.value[trimmed.toLowerCase()] || trimmed
  }

  return {
    groupNameMap,
    resolveGroupName,
  }
}
